import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import Switch from '@mui/material/Switch';
import MFASetupModal from './MFASetupModal';
import { getMFASettings, removeMFAAuthAppSettings } from '../api/MFA';
import { useSelector } from 'react-redux';
import { failed, success } from '../common/Toastify';
import { CircularProgress } from '@mui/material';
import { getOrganization } from '../api/Organization';
import { getPasskeyList, deletePasskey } from '../api/MFA';
import CustomTable from '../common/table/CustomTable';
import { Button } from '../common/Button';
import PasskeyRegistrationModal from './PasskeyRegistrationModal';
import { Modal } from 'react-bootstrap';

const MFALanding = () => {
    const username = useSelector((state) => state?.auth?.user?.email);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [orgSettingsLoading, setOrgSettingsLoading] = useState(false);
    const [isMFAEnabled, setIsMFAEnabled] = useState(false);
    const [mfaModalShow, setMFAModalShow] = useState(false);
    const [orgDefaultMFASettings, setOrgDefaultMFASettings] = useState(false);
    const userRoles = useSelector((state) => state?.auth?.user?.["cognito:groups"]);
    const adminOrgs = useSelector((state) => state?.auth?.user?.organizations);
    const [organizationId, setOrganizationId] = useState("");
    const admin = userRoles?.includes('Super-Admin');
    const [mfaType, setMfaType] = useState("");
    const userData = useSelector((state) => state?.auth?.user);
    const [passkeys, setPasskeys] = useState([]);
    const [loadingPasskeys, setLoadingPasskeys] = useState(false);
    const [registrationModal, setRegistrationModal] = useState(false);
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;
    const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
    const [credentialId, setCredentialId] = useState("");

    useEffect(() => {
        if (!admin && adminOrgs?.[0]?.id) {
            setOrganizationId(adminOrgs[0].id);
        }
    }, [admin, adminOrgs]);

    const fetchPasskeys = useCallback(async () => {
        try {
            const res = await getPasskeyList(userData?.['custom:unique_id']);
            if (res?.body) {
                const { data } = JSON.parse(res.body);
                return data;
            }
            return [];
        } catch (err) {
            console.error('Error fetching passkeys', err);
            return [];
        }
    }, [userData]);

    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const loadData = async () => {
            setLoadingPasskeys(true);
            const data = await fetchPasskeys();
            if (isMounted) {
                setPasskeys(prev => {
                    return JSON.stringify(prev) === JSON.stringify(data) ? prev : data;
                });
                setLoadingPasskeys(false);
            }
        };

        loadData();
        
        const intervalId = setInterval(() => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(loadData, 1000);
        }, 30000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [fetchPasskeys]);

    useEffect(() => {
        setOrgSettingsLoading(true);
        getOrganization(organizationId)
            .then((res) => {
                if (res?.data[0]?.mfaSettings?.enabled === true) {
                    setOrgDefaultMFASettings(true);
                }
            })
            .catch((res) => {
                failed(
                    res?.response?.data?.message ||
                    res?.response?.data?.error ||
                    res.message
                );
            })
            .finally(() => setOrgSettingsLoading(false));
    }, [organizationId]);

    const handleToggle = useCallback(() => {
        if (!isMFAEnabled) {
            setMFAModalShow(true);
        } else {
            setSettingsLoading(true);
            if (!orgDefaultMFASettings && !admin) {
                removeMFAAuthAppSettings({ 
                    accessToken: localStorage?.getItem('accessToken'), 
                    challengeType: mfaType 
                })
                    .then((res) => {
                        if (res?.["$metadata"]?.httpStatusCode === 200) {
                            success("MFA Disabled Successfully");
                            setIsMFAEnabled(false);
                            setMfaType("");
                        }
                    })
                    .catch((res) => {
                        console.log("ERROR IN REMOVING MFA: ", res);
                    })
                    .finally(() => setSettingsLoading(false));
            } else {
                failed("MFA cannot be disabled. Enforced by Organization");
                setSettingsLoading(false);
            }
        }
    }, [isMFAEnabled, orgDefaultMFASettings, admin, mfaType]);

    useEffect(() => {
        setSettingsLoading(true);
        getMFASettings({ username: username?.toLowerCase() })
            .then((res) => {
                if (res?.UserMFASettingList && res.UserMFASettingList.length > 0) {
                    setMfaType(res?.UserMFASettingList[0]);
                    setIsMFAEnabled(true);
                } else {
                    setIsMFAEnabled(false);
                }
            })
            .catch((res) => {
                console.log("ERROR FROM GET MFA SETTINGS: ", res);
            })
            .finally(() => setSettingsLoading(false));
    }, [username]);

    const handleAddPasskey = useCallback(() => {
        setRegistrationModal(true);
    }, []);

    const handleDeletePasskey = useCallback(async (credentialId) => {
        setLoadingPasskeys(true);
        try {
            const res = await deletePasskey(userData?.["custom:unique_id"], credentialId);
            const body = JSON.parse(res?.body);
            if (body?.status) {
                success("Passkey deleted successfully");
                setPasskeys(prev => prev.filter(pk => pk.credentialId !== credentialId));
            } else {
                failed(body?.message || "Failed to delete passkey");
            }
        } catch (err) {
            console.log("ERROR IN DELETING PASSKEY: ", err);
        } finally {
            setLoadingPasskeys(false);
            setDeleteConfirmationModal(false);
        }
    }, [userData]);

    const handleConfirmDelete = useCallback((credentialId) => {
        setCredentialId(credentialId);
        setDeleteConfirmationModal(true);
    }, [userData]);

    const tableData = useMemo(() => {
        return passkeys.map(pk => ({
            id: pk.credentialId, 
            nameTag: pk.nameTag,
            credentialId: pk.credentialId.slice(0, 10) + "...",
            fullCredentialId: pk.credentialId,
            actions: "actions"
        }));
    }, [passkeys]);

    const headCells = useMemo(() => [
        { 
            id: 'nameTag', 
            label: 'Passkey Name', 
            nested: { key: 'nameTag' },
            sort: true 
        },
        { 
            id: 'credentialId', 
            label: 'Credential ID', 
            nested: { key: 'credentialId' },
            sort: true 
        },
        { 
            id: 'actions', 
            label: 'Actions', 
            disableSorting: true 
        }
    ], []);

    const dataFields = useMemo(() => ['nameTag', 'credentialId', 'actions'], []);

    return (
        <section className="common-listing">
            <div className="heading-wrap">
                <h2>MFA Settings</h2>
            </div>

            {(settingsLoading || orgSettingsLoading) ? (
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "80vh"
                }}>
                    <CircularProgress size={50} />
                </div>
            ) : (
                <>
                    <div className="custom-card p-4">
                        <form className="common-form border-fields">
                            <Row>
                                <Col>
                                    <div className="form-group">
                                        <label htmlFor="mfaToggle">Multi Factor Authentication:</label>
                                        <Switch
                                            id="mfaToggle"
                                            checked={isMFAEnabled}
                                            onChange={handleToggle}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            {mfaType !== "" && (
                                <Row>
                                    <Col>
                                        <div style={{ marginTop: "10px" }}>
                                            <span>
                                                <b><span style={{ color: "#015da8" }}>MFA Method Enabled:</span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</b>
                                                {mfaType === "SMS_MFA" ? "SMS based authentication" : "Authenticator App based authentication"}
                                            </span>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </form>
                    </div>
                </>
            )}

            <div className="heading-wrap common-form" style={{ justifyContent: 'space-between', marginTop: '20px' }}>
                <h3>Your Passkeys</h3>
                <Button onClick={handleAddPasskey}> Add Passkey</Button>
            </div>

            <CustomTable
                tableData={tableData}
                headCells={headCells}
                dataFields={dataFields}
                selectedUserAction={({ action, data }) => {
                    if (action === 'delete') {
                        handleConfirmDelete(data.fullCredentialId);
                    }
                }}
                loading={loadingPasskeys}
                rowsPerPage={rowsPerPage}
                page={page}
                setPage={setPage}
                canEdit={false}
                canView={false}
                sortingHeadCellIndex={0} 
                sortingHeadOrder="asc" 
            />

            {mfaModalShow && (
                <MFASetupModal 
                    modalShow={mfaModalShow} 
                    handleClose={() => setMFAModalShow(false)} 
                    user={userData} 
                    token={localStorage?.getItem('authentication')} 
                    showPassKeyRegister={false}
                />
            )}
            {registrationModal && (
                <PasskeyRegistrationModal 
                    modalShow={registrationModal} 
                    handleClose={() => setRegistrationModal(false)} 
                    userId={userData?.["custom:unique_id"]} 
                    orgId={organizationId}
                />
            )}
            {deleteConfirmationModal && (
                <Modal
                    show={deleteConfirmationModal}
                    onHide={() => setDeleteConfirmationModal(false)}
                    backdrop="static"
                    keyboard={false}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Passkey</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to delete this passkey?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setDeleteConfirmationModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={() => handleDeletePasskey(credentialId)}>
                            Delete
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </section>
    );
};

export default React.memo(MFALanding);